import { useEffect, useImperativeHandle, useMemo } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { Stack } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { PhoneNumberDropdown } from '~components';
import InputField from '~components/inputFields/InputField';
import { regexes } from '~constants';
import { findPhoneCountryByPhoneNumber } from '~constants/phoneCountries';
import { useFormContainer } from '~hooks';
import { BaseReference } from '~interfaces';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import schema from './editOrganisationSchema.json';
import OrganisationsService from '../services/organisationsService';

export interface EditOrganisationFormResults {
	name: string;
	emailAddress: string;
	phoneNumber?: string;
}

interface EditOrganisationFormFields {
	name: string;
	emailAddress: string;
	phoneNumber: string;
}
interface EditOrganisationFormProps {
	organisationId?: string;
	onSubmit: (value: EditOrganisationFormResults) => void;
	onCancel: () => void;
}

function parsedPhoneValue(phoneNumber: string) {
	if (!phoneNumber || phoneNumber?.length === 0) {
		return { id: '', name: '' };
	}
	const country = findPhoneCountryByPhoneNumber(phoneNumber);
	if (country) {
		return {
			id: country.phone,
			name: phoneNumber.replace(`+${country.phone}`, ''),
		};
	}
	return { id: '', name: phoneNumber };
}

const organisationsService = getService(OrganisationsService);

const EditOrganisationForm = ({
	organisationId,
	onSubmit,
	onCancel,
}: EditOrganisationFormProps) => {
	const { t } = useTranslation();
	const { setDisabled, formRef } = useFormContainer();
	const inputWidth = '100%';

	// Data loading
	const {
		data: userData,
		error: _userError,
		isLoading: organisationIsLoading,
		isValidating: _organisationIsValidating,
	} = useSWR(
		organisationsService.getOrganisationByIdCacheKey(organisationId),
		([_path, organisationId]) => {
			if (organisationId == undefined) {
				return null;
			}
			return organisationsService.getOrganisationById(organisationId);
		},
		{
			revalidateOnMount: true,
		}
	);

	// Form control
	const { getValues, setValue, watch, control, formState } = useForm<EditOrganisationFormFields>({
		mode: 'onChange',
		resolver: ajvResolver(schema as JSONSchemaType<EditOrganisationFormFields>),
		defaultValues: {
			name: '',
			emailAddress: '',
			phoneNumber: '',
		},
	});
	const phoneNumberValue = watch('phoneNumber') ?? '';
	const phoneNumberError = useMemo(() => {
		const phoneNumber = parsedPhoneValue(phoneNumberValue).name;
		if (phoneNumber.length === 0 || phoneNumber.match(regexes.OnlyNumbers)) {
			return undefined;
		}
		return 'Phone number can only be numbers';
	}, [phoneNumberValue]);

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => {
				const formValues = getValues();
				onSubmit?.({
					name: formValues.name,
					emailAddress: formValues.emailAddress,
					phoneNumber: formValues.phoneNumber.length > 0 ? formValues.phoneNumber : undefined,
				});
			},
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, onCancel, getValues]
	);

	useEffect(() => {
		setDisabled?.(!formState.isValid || phoneNumberError != undefined);
	}, [formState.isValid, phoneNumberError, setDisabled]);

	useEffect(() => {
		if (!organisationIsLoading) {
			setValue('name', userData?.name || '');
			setValue('phoneNumber', userData?.phoneNumber || '');
			setValue('emailAddress', userData?.emailAddress || '');
		}
	}, [setValue, userData, organisationIsLoading]);

	return (
		<Stack spacing={Dimens.spacingLg}>
			<Controller
				name='name'
				control={control}
				render={({ field }) => (
					<InputField
						label={t('organisation.fields.name', { ns: 'entities' })}
						placeholder={t('organisation.fields.name', { ns: 'entities' })}
						sx={{ width: inputWidth }}
						{...field}
						value={field.value}
					/>
				)}
			/>

			<Controller
				name='emailAddress'
				control={control}
				render={({ field }) => (
					<InputField
						label={t('organisation.fields.emailAddress', { ns: 'entities' })}
						placeholder={t('organisation.fields.emailAddress', { ns: 'entities' })}
						sx={{ width: inputWidth }}
						{...field}
						value={field.value}
					/>
				)}
			/>

			<Controller
				name='phoneNumber'
				control={control}
				render={({ field }) => {
					const fieldValue = field.value ?? '';
					const parsedPhone = parsedPhoneValue(fieldValue);

					return (
						<PhoneNumberDropdown
							onValueChanged={(newValue?: BaseReference) => {
								if (newValue && (newValue.name?.length ?? 0) > 0) {
									field.onChange(`+${newValue.id ?? ''}${newValue.name}`);
								} else {
									field.onChange('');
								}
							}}
							sx={{ width: inputWidth }}
							error={phoneNumberError}
							{...field}
							inputValue={{
								id: parsedPhone.id,
								name: parsedPhone.name,
							}}
						/>
					);
				}}
			/>
		</Stack>
	);
};

export default EditOrganisationForm;
